import { useEffect, useState } from 'react';
import { shell } from '../../shell/shellClasses';
import { pulseCalcCsv } from '../debug-export/blockCsv';
import { downloadCsv } from '../debug-export/csv';
import { exportPulseCsv } from './buildHourly';
import { HourlyBarChart } from './HourlyBarChart';
import type { PulseMetricId, PulseSeriesId, SalesPulseModel } from './types';
import { PULSE_SERIES } from './types';

const METRICS: { id: PulseMetricId; label: string }[] = [
  { id: 'gmv', label: 'GMV' },
  { id: 'orders', label: 'Order items' },
  { id: 'units', label: 'Units' },
];

/** FOUNDER-DASHBOARD-IMPLEMENTATION-000005 — SalesPulse24Hours. */
export function SalesPulse24Hours({
  model,
  loading,
  error,
  onRetry,
}: {
  model: SalesPulseModel | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const [metric, setMetric] = useState<PulseMetricId>('gmv');
  const [seriesOn, setSeriesOn] = useState<Set<PulseSeriesId>>(
    () => new Set(PULSE_SERIES.filter((s) => s.defaultOn).map((s) => s.id)),
  );

  useEffect(() => {
    setSeriesOn(new Set(PULSE_SERIES.filter((s) => s.defaultOn).map((s) => s.id)));
  }, [model?.connectionId, model?.dayIso]);

  const toggleSeries = (id: PulseSeriesId) => {
    setSeriesOn((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        if (id === 'today' || next.size <= 1) return prev;
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const onExport = () => {
    if (!model) return;
    downloadCsv(`sales-pulse-24h-${model.dayIso}-${metric}.csv`, exportPulseCsv(model, metric, seriesOn));
    downloadCsv(`dashboard_pulse_${model.dayIso}.csv`, pulseCalcCsv(model));
  };

  return (
    <section
      id="widget-sales-24h"
      data-widget-id="sales-24h"
      aria-labelledby="sales-pulse-heading"
      className="space-y-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2
          id="sales-pulse-heading"
          className="text-sm font-semibold tracking-tight text-[var(--shell-fg)]"
        >
          When did sales happen today?
          {model ? <span className={`ml-2 font-normal ${shell.muted}`}>{model.dayLabel}</span> : null}
        </h2>
        <button
          type="button"
          className={`${shell.control} ${shell.focusRing} border-transparent bg-transparent ${shell.muted} ${shell.hoverRow}`}
          onClick={onExport}
          disabled={!model || loading || !model.hasTodayData}
        >
          Export CSV
        </button>
      </div>

      {/* Metric + series toolbar */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div role="group" aria-label="Pulse metric" className="flex flex-wrap gap-2">
          {METRICS.map((m) => {
            const on = metric === m.id;
            return (
              <button
                key={m.id}
                type="button"
                aria-pressed={on}
                onClick={() => setMetric(m.id)}
                className={`inline-flex min-h-11 items-center rounded-[var(--shell-radius-md)] border px-3 text-xs font-medium ${shell.focusRing} ${
                  on
                    ? 'border-[var(--shell-border)] bg-[var(--shell-hover)] text-[var(--shell-fg)]'
                    : `${shell.border} ${shell.surfaceElevated} ${shell.muted}`
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
        <div role="group" aria-label="Comparison series" className="flex flex-wrap gap-2">
          {PULSE_SERIES.map((s) => {
            const on = seriesOn.has(s.id);
            return (
              <button
                key={s.id}
                type="button"
                aria-pressed={on}
                title={s.label}
                onClick={() => toggleSeries(s.id)}
                className={`inline-flex min-h-11 items-center gap-2 rounded-[var(--shell-radius-md)] border px-2.5 text-xs font-medium ${shell.focusRing} ${
                  on
                    ? 'border-[var(--shell-border)] bg-[var(--shell-hover)] text-[var(--shell-fg)]'
                    : `${shell.border} ${shell.surfaceElevated} ${shell.muted}`
                }`}
              >
                <span
                  className="h-0.5 w-4 shrink-0 rounded-full"
                  style={{
                    background: s.color,
                    borderTop:
                      s.kind === 'line' && s.id !== 'yesterday'
                        ? `1.5px dashed ${s.color}`
                        : undefined,
                  }}
                  aria-hidden
                />
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-[var(--shell-radius-md)] bg-[var(--shell-danger)]/10 px-3 py-2 text-sm text-[var(--shell-danger)]"
        >
          {error}{' '}
          <button type="button" className="underline" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      <div className={`rounded-[var(--shell-radius-lg)] ${shell.surfaceElevated} p-3 sm:p-4`}>
        {loading && !model ? (
          <div
            className="h-[280px] animate-pulse rounded-[var(--shell-radius-md)] bg-[var(--shell-hover)] motion-reduce:animate-none"
            aria-busy
            aria-label="Loading 24 hour sales pulse"
          />
        ) : model && !model.hasTodayData ? (
          <p className={`py-12 text-center text-sm ${shell.muted}`}>
            No hourly sales data available
            <br />
            for the selected period.
          </p>
        ) : model ? (
          <HourlyBarChart model={model} metric={metric} seriesOn={seriesOn} />
        ) : null}
      </div>
    </section>
  );
}
